import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { useToast } from "../lib/toast";

type Step = 1 | 2 | 3;

interface AddedWrestler {
  wrestlerId: string;
  displayName: string;
  floUrl?: string;
}

/**
 * Stepped onboarding for a brand-new parent:
 *   1. Join a team by code, or create one.
 *   2. Add their wrestler(s).
 *   3. Optionally link FloWrestling (and learn about the Reimagined agent)
 *      so match history flows into records/standings.
 */
export function Onboarding() {
  const navigate = useNavigate();
  const toast = useToast();
  const teams = useAsync(() => api.listTeams(), []);

  const [step, setStep] = useState<Step>(1);
  const [busy, setBusy] = useState(false);

  // Step 1 state
  const [codes, setCodes] = useState("");
  const [newTeamName, setNewTeamName] = useState("");
  const [activeTeamId, setActiveTeamId] = useState<string>("");

  // Step 2 state
  const [wName, setWName] = useState("");
  const [wWeight, setWWeight] = useState("");
  const [wClub, setWClub] = useState("");
  const [wFlo, setWFlo] = useState("");
  const [added, setAdded] = useState<AddedWrestler[]>([]);

  const current = teams.data?.items ?? [];

  // --- Step 1 actions ---
  const joinByCode = async () => {
    const list = codes.split(/[\s,]+/).map((c) => c.trim()).filter(Boolean);
    if (list.length === 0) return toast.info("Enter at least one join code.");
    setBusy(true);
    try {
      const res = await api.joinTeams(list);
      const ok = res.filter((r) => r.ok);
      if (ok.length) {
        toast.success(`Joined ${ok.length} team${ok.length === 1 ? "" : "s"}`);
        await teams.reload();
        // Default the active team to the first joined team.
        const joined = res.find((r) => r.ok);
        if (joined) setActiveTeamId((prev) => prev || (joined as { teamId?: string }).teamId || "");
        setCodes("");
        setStep(2);
      } else {
        toast.error("No codes matched. Double-check with your coach.");
      }
    } finally {
      setBusy(false);
    }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return toast.info("Enter a team name.");
    setBusy(true);
    try {
      const t = await api.createTeam(newTeamName.trim());
      toast.success(`Created ${t.name}`);
      await teams.reload();
      setActiveTeamId(t.teamId);
      setNewTeamName("");
      setStep(2);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const continueWithExisting = (teamId: string) => {
    setActiveTeamId(teamId);
    setStep(2);
  };

  // --- Step 2 actions ---
  const addWrestler = async () => {
    if (!wName.trim()) return toast.info("Enter the wrestler's name.");
    setBusy(true);
    try {
      const w = await api.addWrestler({
        displayName: wName.trim(),
        teamId: activeTeamId || undefined,
        weightClass: wWeight.trim() || undefined,
        club: wClub.trim() || undefined,
        floUrl: wFlo.trim() || undefined,
      });
      setAdded((prev) => [
        ...prev,
        { wrestlerId: w.wrestlerId, displayName: w.displayName },
      ]);
      setWName("");
      setWWeight("");
      setWClub("");
      setWFlo("");
      toast.success(`Added ${w.displayName}`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const finish = () => {
    toast.success("You're all set!");
    navigate("/meets");
  };

  return (
    <div>
      <h1>Welcome — let's get set up</h1>
      <div className="wizard-steps">
        {[1, 2, 3].map((n) => (
          <div
            key={n}
            className={"wizard-step" + (step === n ? " active" : step > n ? " done" : "")}
          >
            <span className="wizard-num">{step > n ? "✓" : n}</span>
            <span>{n === 1 ? "Your team" : n === 2 ? "Your wrestlers" : "Match data"}</span>
          </div>
        ))}
      </div>

      {/* Step 1 — team */}
      {step === 1 && (
        <>
          <div className="card">
            <h2>Join your team</h2>
            <p className="muted">
              Enter the join code(s) your coach shared — one per line or
              separated by spaces/commas.
            </p>
            <textarea
              rows={3}
              placeholder={"ABC1234\nXYZ7890"}
              value={codes}
              onChange={(e) => setCodes(e.target.value.toUpperCase())}
            />
            <div className="row" style={{ marginTop: "0.6rem" }}>
              <button onClick={joinByCode} disabled={busy}>
                {busy ? "Joining…" : "Join team"}
              </button>
            </div>
          </div>

          <div className="card">
            <h2>…or create a team</h2>
            <p className="muted">
              Starting a new club or school team? Create it and you'll be its
              admin.
            </p>
            <div className="row">
              <input
                placeholder="Team name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
              />
              <button className="secondary" onClick={createTeam} disabled={busy}>
                Create
              </button>
            </div>
          </div>

          {current.length > 0 && (
            <div className="card">
              <h2>Already on a team</h2>
              {current.map((t) => (
                <div key={t.teamId} className="row spread">
                  <strong>{t.name ?? t.teamId}</strong>
                  <button
                    className="secondary"
                    onClick={() => continueWithExisting(t.teamId)}
                  >
                    Use this →
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Step 2 — wrestlers */}
      {step === 2 && (
        <>
          <div className="card">
            <h2>Add your wrestler(s)</h2>
            <p className="muted">
              Add each of your wrestlers. You can add more than one.
            </p>
            <div className="grid">
              <input
                placeholder="Wrestler name"
                value={wName}
                onChange={(e) => setWName(e.target.value)}
              />
              <div className="row">
                <input
                  placeholder="Weight class (optional)"
                  value={wWeight}
                  onChange={(e) => setWWeight(e.target.value)}
                />
                <input
                  placeholder="Club (optional)"
                  value={wClub}
                  onChange={(e) => setWClub(e.target.value)}
                />
              </div>
              <input
                placeholder="FloWrestling athlete URL (optional)"
                value={wFlo}
                onChange={(e) => setWFlo(e.target.value)}
              />
              <div className="row">
                <button onClick={addWrestler} disabled={busy}>
                  {busy ? "Adding…" : "Add wrestler"}
                </button>
              </div>
            </div>

            {added.length > 0 && (
              <div className="stagger" style={{ marginTop: "0.75rem" }}>
                {added.map((w) => (
                  <div key={w.wrestlerId} className="row spread">
                    <span>✅ {w.displayName}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="row">
            <button onClick={() => setStep(1)} className="secondary">
              ← Back
            </button>
            <button onClick={() => setStep(3)} disabled={added.length === 0}>
              Next →
            </button>
            {added.length === 0 && (
              <span className="muted">Add at least one wrestler to continue.</span>
            )}
          </div>
        </>
      )}

      {/* Step 3 — link match data */}
      {step === 3 && (
        <>
          <div className="card">
            <h2>Match history</h2>
            <p className="muted">
              {added.some(() => true)
                ? "Any FloWrestling links you added will be imported by the companion agent. You can always add or change links later from a wrestler's page."
                : "You can link FloWrestling from a wrestler's page anytime."}
            </p>
          </div>

          <div className="card">
            <h2>Install the match-import agent</h2>
            <p className="muted">
              The companion agent pulls match results from FloWrestling and
              Wrestling Reimagined into each wrestler's record — Reimagined needs
              no URL, it's matched automatically. Install it on your PC
              (PowerShell):
            </p>
            <pre className="code-block">
              irm https://wrestling.corevelocity.io/agent/install.ps1 | iex
            </pre>
          </div>

          <div className="row">
            <button onClick={() => setStep(2)} className="secondary">
              ← Back
            </button>
            <button onClick={finish}>Finish &amp; go to my meets →</button>
          </div>
        </>
      )}

      <p className="muted" style={{ marginTop: "1rem" }}>
        <Link to="/meets" className="link-strong">
          Skip setup for now →
        </Link>
      </p>
    </div>
  );
}
